import chart from './chart';
import { toastApiError } from '@/helpers/apiToast';

// Filtre satiri (helper-footer-bar) grafik alanindan dusulur.
const FILTER_ROW_HEIGHT = 40;
// Planlanan uygulamanin ana rengi (btn-primary), tamamlanan yesil, olay turuncu; ikililer renk
// korlugu kontrolunden geciyor.
const PLANNED_COLOR = '#0088BB';
const COMPLETED_COLOR = '#008300';
const INCIDENT_COLOR = '#eb6834';

export default {
  mixins: [chart],
  data() {
    return {
      year: new Date().getFullYear(),
      // Kartta is emirleri ya da olaylar gosterilir; ikisi ayni eksende okunmuyor (aylik ~450'ye ~15).
      isIncident: false,
      total: [],
      completed: [],
      workGroup: null,
      infrastructure: null,
      workGroups: [],
      infrastructures: [],
      // Gec gelen eski yanit (hizli filtre degisimi ya da kart kapandiktan sonra) yok sayilir.
      requestId: 0,
    };
  },
  computed: {
    workGroupId() {
      return this.workGroup ? this.workGroup.Value : null;
    },
    infrastructureId() {
      return this.infrastructure ? this.infrastructure.Value : null;
    },
    chartHeight() {
      return this.isFullScreen
        ? `calc(100% - ${FILTER_ROW_HEIGHT}px)`
        : `${parseInt(this.heightCalcInner, 10) - FILTER_ROW_HEIGHT}px`;
    },
    // Gerceklesmis son ayin indeksi: gecmis yilda Aralik, bu yil icinde bulunulan ay, gelecek
    // yilda hicbiri (-1). Sonraki aylar henuz yasanmadi.
    lastActualMonth() {
      const now = new Date();
      if (this.year < now.getFullYear()) {
        return 11;
      }
      return this.year > now.getFullYear() ? -1 : now.getMonth();
    },
  },
  async mounted() {
    // Gorunum ve filtreler kartin ayarlarinda saklaniyor (Grid localStorage'a yaziyor). Sayimlar
    // kayitli id'lerle listeler beklenmeden istenir; secim listeler gelince kurulur.
    const saved = this.filterParameters || {};
    const savedWorkGroupId = saved.workGroupId || null;
    const savedInfrastructureId = saved.infrastructureId || null;
    this.isIncident = saved.isIncident === true;
    await Promise.all([this.loadFilterOptions(), this.loadCounts(savedWorkGroupId, savedInfrastructureId)]);
    this.workGroup = this.workGroups.find((x) => x.Value === savedWorkGroupId) || null;
    this.infrastructure = this.infrastructures.find((x) => x.Value === savedInfrastructureId) || null;
    // Kayitli kayit artik listede yoksa secim bos kalir; sayimlar filtresiz yeniden alinir.
    if (this.workGroupId !== savedWorkGroupId || this.infrastructureId !== savedInfrastructureId) {
      this.loadCounts();
    }
  },
  methods: {
    async loadFilterOptions() {
      try {
        const [infrastructureResponse, workGroupResponse] = await Promise.all([
          this.$globalApiClient.infrastructure.infrastructureGetInfrastructures(),
          this.$globalApiClient.workGroup.workGroupGetWorkGroups(),
        ]);
        this.infrastructures = infrastructureResponse.data;
        this.workGroups = workGroupResponse.data;
      } catch (e) {
        toastApiError(e);
      }
    },
    async loadCounts(workGroupId = this.workGroupId, infrastructureId = this.infrastructureId) {
      const requestId = ++this.requestId;
      try {
        // Olaylarin is grubu yok; BE olaylarda is grubunu yok sayiyor.
        const response = await this.$globalApiClient.workOrder.workOrderGetMonthlyCounts({
          year: this.year,
          isIncident: this.isIncident,
          workGroupId,
          infrastructureId,
        });
        if (requestId !== this.requestId) {
          return;
        }
        this.total = response.data.Total;
        this.completed = response.data.Completed;
        this.drawChart();
      } catch (e) {
        toastApiError(e);
      }
    },
    changeYear(step) {
      this.year += step;
      this.loadCounts();
    },
    selectView(isIncident) {
      if (this.isIncident === isIncident) {
        return;
      }
      this.isIncident = isIncident;
      this.filtersChanged();
    },
    filtersChanged() {
      this.$emit('filterParametersUpdated', {
        isIncident: this.isIncident,
        workGroupId: this.workGroupId,
        infrastructureId: this.infrastructureId,
      });
      this.loadCounts();
    },
    chartTitle() {
      // Diger kartlar gibi toplam grafigin kendi basliginda: "2026 - Is Emri Sayisi: 4608".
      const total = this.total.reduce((sum, count) => sum + count, 0);
      return `${this.year} - ${this.translate(this.isIncident ? 'incident_count' : 'work_order_count')}: ${total}`;
    },
    lineDataset(labelKey, data, color) {
      return {
        label: this.translate(labelKey),
        data,
        borderColor: color,
        backgroundColor: color,
        borderWidth: 2,
        fill: false,
        lineTension: 0,
        pointRadius: 4,
        pointHoverRadius: 6,
      };
    },
    buildDatasets() {
      // Henuz yasanmamis aylarda olay ve tamamlanan sayisi 0 degil, bilinmiyor: cizgi o aylara uzanmaz.
      const actual = (values) => values.map((value, month) => (month <= this.lastActualMonth ? value : null));
      if (this.isIncident) {
        return [this.lineDataset('incidents', actual(this.total), INCIDENT_COLOR)];
      }
      // Planlanan is emri gelecek aylarda kesik cizgiyle devam eder (plan); iki parca bulunulan
      // ayda birlesir. Kesik parca lejantta ve o aydaki ipucunda tekrar etmesin diye isaretli.
      const hasUpcoming = this.lastActualMonth < 11;
      const upcoming = this.total.map((value, month) => (hasUpcoming && month >= this.lastActualMonth ? value : null));
      return [
        this.lineDataset('planned_work_orders', actual(this.total), PLANNED_COLOR),
        { ...this.lineDataset('planned_work_orders', upcoming, PLANNED_COLOR), borderDash: [6, 4], isUpcoming: true },
        this.lineDataset('completed_work_orders', actual(this.completed), COMPLETED_COLOR),
      ];
    },
    drawChart() {
      // Chart nesnesi data()'da degil: Vue onu reaktif yapmaya calisirdi.
      if (this.chart) {
        this.chart.data.datasets = this.buildDatasets();
        this.chart.options.title.text = this.chartTitle();
        this.chart.update();
        return;
      }
      if (!this.$refs.canvas) {
        return;
      }
      // Ay adlari ceviri dosyasindan (month_0..11).
      const monthNames = [...Array(12).keys()].map((month) => this.translate(`month_${month}`));
      this.chart = new Chart(this.$refs.canvas, {
        type: 'line',
        data: {
          labels: monthNames.map((name) => name.slice(0, 3)),
          datasets: this.buildDatasets(),
        },
        options: {
          maintainAspectRatio: false,
          legend: {
            position: 'bottom',
            labels: {
              usePointStyle: true,
              filter: (item, data) => !data.datasets[item.datasetIndex].isUpcoming,
            },
          },
          title: {
            display: true,
            text: this.chartTitle(),
          },
          tooltips: {
            mode: 'index',
            intersect: false,
            filter: (item, data) => !(data.datasets[item.datasetIndex].isUpcoming
              && data.datasets[0].data[item.index] != null),
            callbacks: { title: (items) => `${monthNames[items[0].index]} ${this.year}` },
          },
          hover: { mode: 'index', intersect: false },
          scales: {
            yAxes: [{
              // Sayim ekseni: ara degerlerde (2.5 gibi) tick cikmasin.
              ticks: { beginAtZero: true, callback: (value) => (Number.isInteger(value) ? value : undefined) },
            }],
          },
          onClick: (event, elements) => this.openMonth(elements),
        },
      });
    },
    openMonth(elements) {
      // Olay listesi tarih araligi almiyor; aya tiklayinca yalnizca is emri listesine gidilir.
      if (this.isIncident || !elements || elements.length === 0) {
        return;
      }
      const month = moment({ year: this.year, month: elements[0]._index });
      this.$router.push({
        name: 'WorkOrder',
        query: {
          startDate: month.clone().startOf('month').valueOf(),
          endDate: month.clone().endOf('month').valueOf(),
        },
      });
    },
    redraw() {
      if (this.chart) {
        this.chart.resize();
      }
    },
  },
  beforeDestroy() {
    // Yolda olan istegin yaniti artik cizmesin; kapanmis kartin grafigi de birakilir.
    this.requestId += 1;
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  },
};
